import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import LazyComponent from '@components/LazyComponent';
import OnVisibleInViewport from '@components/OnVisibleInViewport';
import { divideSections, getDefaultSectionLength } from './utils';

const DEFAULT_ITEM_HEIGHT = '150px';
const DEFAULT_ITEMS_PER_SECTION = 8;

function LazyList({
  children,
  dataId,
  data,
  fetchMore,
  hasMore,
  loader,
  itemsPerSection,
  defaultItemHeight,
  thresholdMargin,
  onSectionHeightUpdated,
  initialSectionsHeights,
  getRefs,
  isDynamic,
}) {
  const sections = useMemo(() => divideSections(data, itemsPerSection), [
    data,
    itemsPerSection,
  ]);

  const getSectionRef = useCallback(
    (section, el) => {
      if (getRefs && dataId) {
        section.forEach(s => {
          const sectionId = s?.[dataId];
          if (sectionId) getRefs(sectionId, el);
        });
      }
    },
    [getRefs, dataId],
  );

  return (
    <>
      {sections.map((section, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} ref={el => getSectionRef(section, el)}>
          <LazyComponent
            id={i}
            thresholdMargin={thresholdMargin}
            defaultHeight={getDefaultSectionLength(section, defaultItemHeight)}
            height={initialSectionsHeights[i]}
            onHeightUpdated={onSectionHeightUpdated}
            count={section.length}
            isDynamic={isDynamic}
          >
            {/* We pass LazyComponent a function, so section.map and all children
            are executed only when LazyComponent is visible on viewport. */}
            {() =>
              section.map((item, index) => {
                const nextItemOnCurrentSection = section[index + 1];
                const nextSection = sections[i + 1];
                const isLast = !nextItemOnCurrentSection && !nextSection;

                return children(item, index, isLast);
              })
            }
          </LazyComponent>
        </div>
      ))}

      {Boolean(hasMore && loader && fetchMore) && (
        <OnVisibleInViewport onVisible={fetchMore}>
          {loader}
        </OnVisibleInViewport>
      )}
    </>
  );
}

LazyList.propTypes = {
  hasMore: PropTypes.bool,
  dataId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  data: PropTypes.array.isRequired,
  itemsPerSection: PropTypes.number,
  defaultItemHeight: PropTypes.string,
  thresholdMargin: PropTypes.string,
  initialSectionsHeights: PropTypes.object,
  children: PropTypes.func.isRequired,
  loader: PropTypes.node,
  fetchMore: PropTypes.func,
  onSectionHeightUpdated: PropTypes.func,
  getRefs: PropTypes.func,
  isDynamic: PropTypes.bool,
};

LazyList.defaultProps = {
  hasMore: false,
  loader: null,
  itemsPerSection: DEFAULT_ITEMS_PER_SECTION,
  thresholdMargin: DEFAULT_ITEM_HEIGHT,
  initialSectionsHeights: {},
  isDynamic: false,
};

export default LazyList;
